import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import React, { useEffect, useMemo, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { AuthorRoles, hasEscalationEnded, IChatMessage } from '../../../libs/models/ChatMessage';
import { ChatHistoryItem } from './ChatHistoryItem';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { ChatStatusCard } from '../ChatStatusCard';
import { BotResponseStatus } from '../../../redux/features/conversations/ChatState';
import { ChatSupplementaryQuestions } from '../ChatSupplementaryQuestions';
import { DiscoveryQuestions } from './DiscoveryQuestions';
import { useDiscovery } from '../../../libs/hooks/useDiscovery';
import { useGuidanceCard } from '../../../libs/hooks/useGuidanceCard';
import { InlineCsatWidget } from './InlineCsatWidget';
import { InlineEscalationOffer } from './InlineEscalationOffer';
import { inlineCsatMessage } from './inlineCsatMessage';
import { lastUserMessageBefore } from './lastUserMessageBefore';
import { SpirisApprovalCard } from './SpirisApprovalCard';
import { SpirisConnectCard } from './SpirisConnectCard';
import { CustomerLiveChatService } from '../../../libs/services/CustomerLiveChatService';
import { EscalationOfferEntry, setEscalationOffer } from '../../../redux/features/livechat/escalationOfferSlice';

const useClasses = makeStyles({
    root: {
        gap: tokens.spacingVerticalM,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '900px',
        width: '100%',
        justifySelf: 'center',
        '@media (min-width:1100px) and (max-width:1700px)': {
            width: '80%',
        },
        '@media (max-width:1100px)': {
            width: '100%',
        },
    },
    messageWrapper: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'start',
        gap: '10px',
        marginBottom: '10px',
    },
    item: {
        display: 'flex',
        flexDirection: 'column',
    },
    bot: {
        alignSelf: 'end',
        width: '100%',
        scrollMarginTop: tokens.spacingVerticalM,
    },
    // A Spiris card belongs under its answer, not beside it in the row.
    withSpirisCard: {
        flexDirection: 'column',
        alignItems: 'stretch',
    },
    spirisCard: {
        maxWidth: '640px',
    },
});

interface ChatHistoryProps {
    onQuerySubmit: (query: string) => void;
    onQuestionButtonClick: (text: string) => void;
    statuses: BotResponseStatus[];
}

export const ChatHistory: React.FC<ChatHistoryProps> = ({ onQuerySubmit, statuses, onQuestionButtonClick }) => {
    const classes = useClasses();
    const dispatch = useAppDispatch();
    const auth = useAuth();
    const token = auth.user?.access_token;
    const isBot = (message: IChatMessage) => message.authorRole === AuthorRoles.Bot;
    const { DynamicReport, EnableInChatCsat, LiveChatEnabled, LiveChatProactiveOfferEnabled } = useAppSelector(
        (state: RootState) => state.app.serviceInfo.featureFlags,
    );
    // The proactive offer needs the master switch AND its own dedicated switch.
    const proactiveOfferEnabled = LiveChatEnabled && LiveChatProactiveOfferEnabled;
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const escalationOffer = useAppSelector(
        (state: RootState) => state.escalationOffer.offers[selectedId] as EscalationOfferEntry | undefined,
    );
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    // Latest-value ref so the offer fetch can send hostContext as a header without making the whole
    // (spread-merged, new-identity-per-page-update) object an effect trigger — the fetch stays
    // once-per-chat keyed on selectedId rather than refiring on unrelated page-context changes.
    const hostContextRef = useRef(hostContext);
    hostContextRef.current = hostContext;
    const messages = conversations[selectedId].messages;
    const hasStartedTyping = conversations[selectedId].input != '';
    const { isEnabled: discoveryEnabled } = useDiscovery();
    const { showCard } = useGuidanceCard();

    const handleQuestionButtonClick = (text: string) => {
        onQuestionButtonClick(text);
        onQuerySubmit(text);
    };

    const lastBotIndex = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            if (isBot(messages[i])) return i;
        }
        return -1;
    }, [messages]);

    const supplementaryItems = useMemo(() => {
        if (showCard && messages.length === 1) {
            return [];
        }

        if (discoveryEnabled) {
            // For DynamicReport, show followUpAsks for all messages except the first one
            return messages.length > 1 ? messages[messages.length - 1].followUpAsks : [];
        }
        // For non-DynamicReport, keep the current behavior
        return messages.length > 0 ? messages[messages.length - 1].followUpAsks : [];
    }, [messages, discoveryEnabled, showCard]);

    // Render the widget on the bot message a server trigger (sentiment/resolution) flagged. Whether
    // it stays visible after a response is now owned by the widget itself (#1767): the tab the user
    // acted in keeps a short "thank you" beat before collapsing, while other open tabs — and reloads
    // — hide immediately off the persisted/broadcast csatSubmitted flag. The backend gate guarantees
    // at most one message is flagged per session, so this is a single offer (#1641).
    const csatMessage = inlineCsatMessage(messages, EnableInChatCsat);
    const inlineCsatMessageId = csatMessage?.id;
    const inlineCsatAnswered = csatMessage?.csatSubmitted === true;

    // Reload survival for the proactive human-handoff offer: fetch the active offer when a chat is
    // opened so a card surfaced before a refresh reappears. The live appearance comes via SignalR.
    // Gated on the LiveChatEnabled master switch so we don't hit the LiveChat endpoint when the
    // feature is off (or its module isn't deployed). Runs once per chat — selectedId is the dep.
    useEffect(() => {
        if (!proactiveOfferEnabled || !selectedId || !token) {
            return;
        }
        let active = true;
        const service = new CustomerLiveChatService(token);
        service
            .getOfferAsync(selectedId, hostContextRef.current)
            .then((offer) => {
                if (active && offer.messageId) {
                    dispatch(setEscalationOffer({ chatId: selectedId, messageId: offer.messageId }));
                }
            })
            .catch((err: unknown) => {
                // Best-effort: a failed offer lookup just means no card until the live push (if any).
                if (active) {
                    console.error('LiveChat: failed to fetch escalation offer', err);
                }
            });
        return () => {
            active = false;
        };
    }, [proactiveOfferEnabled, selectedId, token, dispatch]);

    const escalationEnded = useMemo(() => hasEscalationEnded(messages), [messages]);
    const showEscalationOffer =
        proactiveOfferEnabled && !!escalationOffer && !escalationOffer.dismissed && !escalationEnded;

    return (
        <div className={classes.root}>
            {messages.map((message, index) => (
                <div
                    key={message.timestamp}
                    data-last-bot-message={index === lastBotIndex ? 'true' : undefined}
                    className={
                        isBot(message)
                            ? mergeClasses(
                                  classes.messageWrapper,
                                  classes.bot,
                                  !!message.spirisPrompt && classes.withSpirisCard,
                              )
                            : classes.messageWrapper
                    }
                >
                    <ChatHistoryItem message={message} messageIndex={index} />
                    {isBot(message) && message.spirisPrompt?.kind === 'approval' && (
                        <div className={classes.spirisCard}>
                            <SpirisApprovalCard chatId={selectedId} prompt={message.spirisPrompt} />
                        </div>
                    )}
                    {/* Only the latest bot answer offers a connect: an older card is stale once the chat moved on. */}
                    {index === lastBotIndex && message.spirisPrompt?.kind === 'connect' && (
                        <div className={classes.spirisCard}>
                            <SpirisConnectCard
                                reason={message.spirisPrompt.reason}
                                activationUrl={message.spirisPrompt.activationUrl}
                                retryMessage={lastUserMessageBefore(messages, index)}
                                onRetry={onQuerySubmit}
                            />
                        </div>
                    )}
                </div>
            ))}
            {inlineCsatMessageId && (
                <InlineCsatWidget chatId={selectedId} messageId={inlineCsatMessageId} answered={inlineCsatAnswered} />
            )}
            {showEscalationOffer && <InlineEscalationOffer chatId={selectedId} />}
            <ChatSupplementaryQuestions onClick={handleQuestionButtonClick} items={supplementaryItems} />
            {discoveryEnabled && !showCard && messages.length === 1 && !hasStartedTyping && (
                <DiscoveryQuestions onQuerySubmit={onQuerySubmit} onQuestionButtonClick={onQuestionButtonClick} />
            )}
            {DynamicReport && <ChatStatusCard statuses={statuses} />}
        </div>
    );
};
